import { CommonModule } from '@angular/common';
import { Component, ElementRef, Input, OnChanges, SimpleChanges, ViewChild } from '@angular/core';
import { CheckboxModule } from 'primeng/checkbox';
import { RadioButtonModule } from 'primeng/radiobutton';
import { StepperModule } from 'primeng/stepper';
import { ButtonModule } from 'primeng/button';
import { buildPublicExperienceStyles } from '../../../services/global.service';
import { Product, ProductDesignTemplatePageModuleDataContent } from '../../../store/app.model';
import { Store } from '@ngxs/store';
import { QrProductFeedback } from '../../../store/app.actions';
import { ActivatedRoute } from '@angular/router';
import { NotificationService } from '../../../services/notification.service';
import { FormsModule } from '@angular/forms';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { InputDialogComponent } from '../input-dialog/input-dialog.component';

@Component({
  selector: 'app-survey',
  imports: [CommonModule, CheckboxModule, RadioButtonModule, StepperModule, ButtonModule, FormsModule],
  templateUrl: './survey.component.html',
})
export class SurveyComponent implements OnChanges {
  @ViewChild('surveyForm') surveyForm!: ElementRef<HTMLFormElement>;
  @Input({ required: true }) content!: any;
  @Input({ required: true }) styles!: any;
  @Input({ required: true }) isSelectedModule!: boolean;
  @Input({ required: true }) isFirstModule!: boolean;
  @Input({ required: true }) isLastModule!: boolean;
  @Input({ required: true }) product!: Product;

  items: ProductDesignTemplatePageModuleDataContent[] = [];
  tenantId: string = 'regenesis';
  answers: Record<string, any> = {};
  activeStep: number = 1;
  ref: DynamicDialogRef | null = null;

  constructor(
    private store: Store,
    private route: ActivatedRoute,
    public dialogService: DialogService,
    private notificationService: NotificationService
  ) {
    this.tenantId = this.route.snapshot.queryParams['x-tenant-id'] || 'regenesis';
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.content) {
      if ('pageModuleTemplateDataContents' in this.content && Array.isArray(this.content.pageModuleTemplateDataContents)) {
        this.items = this.content.pageModuleTemplateDataContents;
      } else if ('contentItems' in this.content && Array.isArray(this.content.contentItems)) {
        this.items = this.content.contentItems;
      } else if ('components' in this.content && Array.isArray(this.content.components)) {
        this.items = this.content.components;
      } else {
        this.items = [];
      }
    } else {
      this.items = [];
    }
  }

  filter(array: any[] | undefined | null, column: any, value: any) {
    if (!array) return [];
    return array.filter(item => item[column] === value);
  }

  buildStyle(type: string, style: any): string {
    return buildPublicExperienceStyles(type, style);
  }

  onSelectionChange(question: any, value: any) {
    this.answers[`${question.order}-${question.value.text}`] = value;
    console.log(this.answers);
  }

  isQuestionAnswered(question: any): boolean {
    const answer = this.answers[`${question.order}-${question.value.text}`];
    if (!answer) return false;
    
    if (question.value.question_type === 'multiple') {
      return Array.isArray(answer) && answer.length > 0;
    } else {
      return answer !== null && answer !== undefined && answer !== '';
    }
  }

  canMoveToNextStep(currentStepIndex: number): boolean {
    const questions = this.filter(this.items, 'type', 'question');
    if (currentStepIndex < 1 || currentStepIndex > questions.length) return false;
    return this.isQuestionAnswered(questions[currentStepIndex - 1]);
  }

  goToNextStep(currentStepIndex: number, activateCallback: (step: number) => void) {
    if (this.canMoveToNextStep(currentStepIndex)) {
      activateCallback(currentStepIndex + 1);
      this.activeStep = currentStepIndex + 1;
    }
  }

  goToPreviousStep(currentStepIndex: number, activateCallback: (step: number) => void) {
    if (currentStepIndex > 1) {
      activateCallback(currentStepIndex - 1);
      this.activeStep = currentStepIndex - 1;
    }
  }

  canSubmitSurvey(): boolean {
    const questions = this.filter(this.items, 'type', 'question');
    return questions.length > 0 && questions.every(q => this.isQuestionAnswered(q));
  }

  submitSurvey(event: Event) {
    event.preventDefault();
    const surveyData = this.formatSurveyResponse();
  
    this.ref = this.dialogService.open(InputDialogComponent, {
      width: '90%',
      showHeader: false,
      modal: true,
    });

    if (this.ref) {
      this.ref.onClose.subscribe((result: string | null) => {
        if (result) {
          this.submitSurveyData(result, surveyData);
        }
      });
    }
  }  

  submitSurveyData(email: string, surveyData: any) {
    // Save current scroll position
    const scrollPosition = window.scrollY || document.documentElement.scrollTop;
    
    this.store.dispatch(
      new QrProductFeedback(
        {
          type: 'SURVEY',
          qrProductItemId: this.product.qrProductItem.id,
          email,
          response: surveyData
        },
        this.tenantId
      )
    ).subscribe(() => {
      this.notificationService.openSuccessToast("Your response has been submitted successfully!");
      this.answers = {};
      this.activeStep = 1;
      this.surveyForm.nativeElement.reset();
      
      // Restore scroll position after reset
      setTimeout(() => {
        window.scrollTo(0, scrollPosition);
      }, 0);
    });
  }

  formatSurveyResponse() {
    const questions = this.filter(this.items, 'type', 'question');
    return {
      questions: questions.map(question => ({
        questionId: question.id,
        questionText: question.value.text,
        questionType: question.value.question_type || 'single',
        selectedOptions: Array.isArray(this.answers[`${question.order}-${question.value.text}`]) 
          ? this.answers[`${question.order}-${question.value.text}`] 
          : [this.answers[`${question.order}-${question.value.text}`]].filter(Boolean),
        availableOptions: question.value.options || []
      })),
      totalQuestions: questions.length,
      timestamp: new Date().toISOString()
    };
  }

  canAccessStep(stepNumber: number): boolean {
    const questions = this.filter(this.items, 'type', 'question');
    if (stepNumber < 1 || stepNumber > questions.length) return false;
    
    // Can always access step 1
    if (stepNumber === 1) return true;
    
    // Can access a step only if all previous steps are answered
    for (let i = 0; i < stepNumber - 1; i++) {
      if (!this.isQuestionAnswered(questions[i])) {
        return false;
      }
    }
    return true;
  }

  handleStepClick(stepNumber: number, activateCallback: (step: number) => void) {
    if (this.canAccessStep(stepNumber)) {
      activateCallback(stepNumber);
      this.activeStep = stepNumber;
    }
  }
}
